import { useState, useMemo, useEffect } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { geoBounds, geoContains } from 'd3-geo';
import { feature } from 'topojson-client';

// us-atlas counties topojson uses FIPS state codes (first 2 digits of county FIPS).
// To filter to specific states, we filter geographies where geo.id (5-digit
// county FIPS) starts with the state's FIPS prefix.
const STATE_CODE_TO_FIPS: Record<string, string> = {
  AL: '01', AK: '02', AZ: '04', AR: '05', CA: '06', CO: '08', CT: '09',
  DE: '10', DC: '11', FL: '12', GA: '13', HI: '15', ID: '16', IL: '17',
  IN: '18', IA: '19', KS: '20', KY: '21', LA: '22', ME: '23', MD: '24',
  MA: '25', MI: '26', MN: '27', MS: '28', MO: '29', MT: '30', NE: '31',
  NV: '32', NH: '33', NJ: '34', NM: '35', NY: '36', NC: '37', ND: '38',
  OH: '39', OK: '40', OR: '41', PA: '42', RI: '44', SC: '45', SD: '46',
  TN: '47', TX: '48', UT: '49', VT: '50', VA: '51', WA: '53', WV: '54',
  WI: '55', WY: '56',
};
const FIPS_TO_STATE_CODE: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_CODE_TO_FIPS).map(([k, v]) => [v, k]),
);

export interface CountyOwner {
  id: string;
  name: string;
  countyFips: string[];
  color: string;
}

interface UsCountyMapProps {
  // 2-letter state codes to render. Other states are hidden.
  visibleStates: string[];
  // Each owner's claimed counties (by FIPS code)
  owners?: CountyOwner[];
  // Selected county FIPS (highlighted blue)
  selectedFips?: string | null;
  // Called when user clicks a county
  onCountyClick?: (fips: string, name: string, stateCode: string) => void;
  // Optional: visually highlight a single county (for ZIP lookup result)
  spotlightFips?: string | null;
  // Optional: editor mode shows draft selection in purple, ignores owners
  editMode?: boolean;
  draftFips?: Set<string>;
  // Optional: dealer (or generic) counts per county. When set, the tooltip
  // shows "N dealers" and counties with >0 get a density shade.
  countsByFips?: Record<string, number>;
}

// Visible on the slate-800 page background — slate-800 fill was effectively
// invisible. Slate-500 gives clear contrast while still reading as "unclaimed."
const UNCLAIMED_FILL = '#64748b';      // slate-500
const HOVER_FILL = '#facc15';          // yellow-400
const DRAFT_FILL = '#9333ea';          // purple-600
const SPOTLIGHT_STROKE = '#06b6d4';    // cyan-500

export default function UsCountyMap({
  visibleStates, owners = [], selectedFips, onCountyClick, spotlightFips,
  editMode = false, draftFips, countsByFips,
}: UsCountyMapProps) {
  // Density max across visible counties so the shading is relative to the
  // zoomed area, not the whole country.
  const maxCount = useMemo(() => {
    if (!countsByFips) return 0;
    let max = 0;
    for (const v of Object.values(countsByFips)) if (v > max) max = v;
    return max;
  }, [countsByFips]);
  function densityFillFor(fips: string): string | null {
    if (!countsByFips || maxCount === 0) return null;
    const n = countsByFips[fips] || 0;
    if (n === 0) return null;
    const intensity = n / maxCount;
    if (intensity > 0.75) return '#1d4ed8';   // blue-700
    if (intensity > 0.5)  return '#3b82f6';   // blue-500
    if (intensity > 0.25) return '#60a5fa';   // blue-400
    return '#93c5fd';                          // blue-300
  }
  const [hover, setHover] = useState<{ fips: string; name: string; stateCode: string } | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const [countyFeatures, setCountyFeatures] = useState<any[]>([]);

  // Load and parse the topojson once so we can do point-in-polygon for ZIP lookups
  useEffect(() => {
    fetch('/us-counties-10m.json')
      .then(r => r.json())
      .then((topo: any) => {
        const fc = feature(topo, topo.objects.counties) as any;
        setCountyFeatures(fc.features || []);
      })
      .catch(err => console.error('Failed to load county topojson:', err));
  }, []);

  // FIPS → owner index for fast color lookup
  const ownersByFips = useMemo(() => {
    const map: Record<string, CountyOwner[]> = {};
    for (const owner of owners) {
      for (const fips of owner.countyFips) {
        (map[fips] ||= []).push(owner);
      }
    }
    return map;
  }, [owners]);

  const visibleFipsPrefixes = useMemo(
    () => new Set(visibleStates.map(s => STATE_CODE_TO_FIPS[s]).filter(Boolean)),
    [visibleStates],
  );

  function fillFor(fips: string): string {
    if (editMode && draftFips?.has(fips)) return DRAFT_FILL;
    if (selectedFips === fips) return '#1d4ed8';
    const ownersHere = ownersByFips[fips] || [];
    if (ownersHere.length > 0) {
      if (ownersHere.length === 1) return ownersHere[0].color;
      return '#f59e0b'; // amber for multi-owner overlap
    }
    // No owners → if dealer counts available, shade by density
    const densityColor = densityFillFor(fips);
    if (densityColor) return densityColor;
    return UNCLAIMED_FILL;
  }

  // Compute projection bounds from the visible features' bounding box. Using
  // a fixed-size 960x600 viewBox we figure out a scale that fits all visible
  // counties with a small margin. We deliberately do NOT use geoAlbersUsa
  // when zoomed in — it ignores center/scale and forces the whole-country
  // view. geoMercator respects center+scale and renders fine for a single
  // state or small region. For multi-state firm overviews we still use
  // geoAlbersUsa since that's its sweet spot.
  const { projection, projectionCenter, scale } = useMemo(() => {
    if (!countyFeatures.length || visibleStates.length === 0) {
      return { projection: 'geoAlbersUsa' as const, projectionCenter: null, scale: 1000 };
    }
    const visible = countyFeatures.filter(f => visibleFipsPrefixes.has(String(f.id).slice(0, 2)));
    if (visible.length === 0) {
      return { projection: 'geoAlbersUsa' as const, projectionCenter: null, scale: 1000 };
    }
    // If we're zoomed to a small number of states use a state-aware projection
    if (visibleStates.length > 4) {
      return { projection: 'geoAlbersUsa' as const, projectionCenter: null, scale: 1100 };
    }

    // Get bbox: [minLng, minLat, maxLng, maxLat] across visible features
    let minLng = 180, minLat = 90, maxLng = -180, maxLat = -90;
    for (const f of visible) {
      const b = geoBounds(f);
      if (b[0][0] < minLng) minLng = b[0][0];
      if (b[0][1] < minLat) minLat = b[0][1];
      if (b[1][0] > maxLng) maxLng = b[1][0];
      if (b[1][1] > maxLat) maxLat = b[1][1];
    }
    const centerLng = (minLng + maxLng) / 2;
    const centerLat = (minLat + maxLat) / 2;
    const spanLng = Math.max(0.1, maxLng - minLng);
    const spanLat = Math.max(0.1, maxLat - minLat);
    // Fit the wider of two ratios; 960x600 viewBox with ~10% margin.
    // Mercator scale ~ 57 → 1 degree per unit at low lats; pick what fits.
    const fitScaleX = (960 * 0.85) / (spanLng * (Math.PI / 180));
    const fitScaleY = (600 * 0.85) / (spanLat * (Math.PI / 180));
    const fitScale = Math.min(fitScaleX, fitScaleY);
    return {
      projection: 'geoMercator' as const,
      projectionCenter: [centerLng, centerLat] as [number, number],
      scale: fitScale,
    };
  }, [countyFeatures, visibleFipsPrefixes, visibleStates]);

  return (
    <div className="relative">
      <div className="w-full" style={{ aspectRatio: '16 / 10', maxHeight: 520 }}>
        <ComposableMap
          projection={projection}
          projectionConfig={{
            scale,
            ...(projectionCenter ? { center: projectionCenter } : {}),
          }}
          width={960}
          height={600}
          style={{ width: '100%', height: '100%' }}
        >
          <Geographies geography="/us-counties-10m.json">
            {({ geographies }) =>
              geographies
                .filter((geo: any) => visibleFipsPrefixes.has(String(geo.id).slice(0, 2)))
                .map((geo: any) => {
                  const fips = String(geo.id);
                  const stateCode = FIPS_TO_STATE_CODE[fips.slice(0, 2)] || '';
                  const name = geo.properties?.name || 'County';
                  const fill = fillFor(fips);
                  const isSpotlight = spotlightFips === fips;
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onMouseEnter={() => setHover({ fips, name, stateCode })}
                      onMouseMove={(evt: any) => {
                        const container = (evt.currentTarget as SVGElement)
                          .closest('.relative')
                          ?.getBoundingClientRect();
                        if (container) {
                          setTooltipPos({
                            x: evt.clientX - container.left + 12,
                            y: evt.clientY - container.top + 12,
                          });
                        }
                      }}
                      onMouseLeave={() => { setHover(null); setTooltipPos(null); }}
                      onClick={() => onCountyClick?.(fips, name, stateCode)}
                      style={{
                        default: {
                          fill,
                          stroke: isSpotlight ? SPOTLIGHT_STROKE : '#e2e8f0',
                          strokeWidth: isSpotlight ? 2 : 0.8,
                          outline: 'none',
                          cursor: onCountyClick ? 'pointer' : 'default',
                        },
                        hover: {
                          fill: editMode && draftFips?.has(fips) ? '#7c3aed' : HOVER_FILL,
                          stroke: isSpotlight ? SPOTLIGHT_STROKE : '#f8fafc',
                          strokeWidth: isSpotlight ? 2.5 : 1.4,
                          outline: 'none',
                          cursor: onCountyClick ? 'pointer' : 'default',
                        },
                        pressed: { fill: '#1d4ed8', outline: 'none' },
                      }}
                    />
                  );
                })
            }
          </Geographies>
        </ComposableMap>
      </div>

      {hover && tooltipPos && (() => {
        const ownersHere = ownersByFips[hover.fips] || [];
        const dealerCount = countsByFips?.[hover.fips] || 0;
        return (
          <div
            className="absolute pointer-events-none bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap z-10"
            style={{ left: tooltipPos.x, top: tooltipPos.y }}
          >
            <strong>{hover.name}</strong>, {hover.stateCode}
            {countsByFips && (
              <div className={`text-xs font-semibold mt-0.5 ${dealerCount > 0 ? 'text-blue-300' : 'text-slate-400'}`}>
                {dealerCount} dealer{dealerCount === 1 ? '' : 's'}
              </div>
            )}
            {ownersHere.length > 0 && (
              <div className="text-[10px] opacity-90 mt-0.5">
                {ownersHere.map(o => o.name).join(' + ')}
              </div>
            )}
            <div className="text-[10px] opacity-60 mt-0.5">
              FIPS {hover.fips}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/**
 * Find which county a [longitude, latitude] point falls in.
 * Returns { fips, name, stateCode } or null. Exported for ZIP-search flow.
 */
export function findCountyForCoords(features: any[], lng: number, lat: number) {
  for (const f of features) {
    if (geoContains(f, [lng, lat])) {
      const fips = String(f.id);
      const stateCode = FIPS_TO_STATE_CODE[fips.slice(0, 2)] || '';
      return { fips, name: f.properties?.name || 'County', stateCode };
    }
  }
  return null;
}

export { STATE_CODE_TO_FIPS, FIPS_TO_STATE_CODE };
